import React, { useState, useRef, useEffect } from "react";
import { BsTrash, BsPencil, BsPlus, BsDash } from "react-icons/bs";
import axios from 'axios';
import prescriptionService from '../services/prescriptionService';
import { 
  API_BASE_URL, 
  API_PATIENT_LIST, 
  API_DOCTOR_LIST, 
  API_INVENTORY,
  API_BASE_HEALTH_CENTER_URL,
  API_HEALTH_CENTERS,
  API_TREATMENTM_LIST,
  API_LAB_TEMPLATES_LIST,
  buildQueryString
} from '../../../helpers/urlHelper';

// Estados iniciales actualizados según el nuevo modelo
const EMPTY_PRESCRIPTION_STATE = {
  doctorId: '',
  patientId: '',
  clinicId: '',
  symptoms: '',
  suggestion: '',
  condition: {
    conditionname: '',
    currentstatus: 'ongoing',
    reportedon: new Date().toISOString().split('T')[0],
    resolvedon: '',
    comments: ''
  },
  diseases: [],
  medicines: [],
  labtests: []
};

const INITIAL_DISEASE_STATE = {
  diseaseId: '',
  name: '',
  description: '',
  severity: 'moderate',
  duration: ''
};

const INITIAL_MEDICINE_STATE = {
  medicineId: '',
  name: '',
  dosage: '',
  frequency: '',
  duration: '',
  purpose: ''
};

const INITIAL_LABTEST_STATE = {
  labtestId: '',
  name: '',
  result: '',
  date: new Date().toISOString().split('T')[0]
};

// Componentes auxiliares
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center my-3">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
);

const AlertMessage = ({ type, message }) => (
  <div className={`alert alert-${type}`} role="alert">
    {message}
  </div>
);

// Componente para una prescripción individual en el modo multi
// Componente para una prescripción individual en el modo multi - CORREGIDO
const PrescriptionCard = ({ 
  prescription, 
  index, 
  onUpdate, 
  onRemove, 
  patients, 
  doctors, 
  clinics, 
  diseases, 
  medicines, 
  labtests,
  isExpanded,
  onToggleExpand
}) => {
  // CORRECCIÓN: Usar un único estado para todos los formularios temporales
  const [tempStates, setTempStates] = useState({
    disease: {...INITIAL_DISEASE_STATE},
    medicine: {...INITIAL_MEDICINE_STATE},
    labtest: {...INITIAL_LABTEST_STATE}
  });

  // DEBUG: Agregar logs para debugging
  useEffect(() => {
    console.log(`PrescriptionCard ${index} - prescription:`, prescription);
  }, [prescription, index]);

  const handleInputChange = (e) => {
    console.log(`handleInputChange - Card ${index}:`, e.target.name, '=', e.target.value);
    const { name, value } = e.target;
    const newPrescription = { ...prescription, [name]: value };
    console.log(`Calling onUpdate for Card ${index} with:`, newPrescription);
    onUpdate(index, newPrescription);
  };

  const handleConditionChange = (e) => {
    console.log(`handleConditionChange - Card ${index}:`, e.target.name, '=', e.target.value);
    const { name, value } = e.target;
    const newPrescription = {
      ...prescription,
      condition: {
        ...prescription.condition,
        [name]: value
      }
    };
    console.log(`Calling onUpdate for condition in Card ${index} with:`, newPrescription);
    onUpdate(index, newPrescription);
  };

  // CORRECCIÓN: Funciones para manejar estados temporales
  const updateTempState = (type, field, value) => {
    setTempStates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const resetTempState = (type) => {
    setTempStates(prev => ({
      ...prev,
      [type]: type === 'disease' ? {...INITIAL_DISEASE_STATE} :
              type === 'medicine' ? {...INITIAL_MEDICINE_STATE} :
              {...INITIAL_LABTEST_STATE}
    }));
  };

  const addDisease = () => {
    const disease = tempStates.disease;
    if (!disease.name && !disease.diseaseId) {
      alert('Por favor completa el nombre de la enfermedad');
      return;
    }

    onUpdate(index, {
      ...prescription,
      diseases: [...prescription.diseases, { ...disease, id: Date.now() + Math.random() }]
    });
    resetTempState('disease');
  };

  const removeDisease = (diseaseId) => {
    onUpdate(index, {
      ...prescription,
      diseases: prescription.diseases.filter(d => d.id !== diseaseId)
    });
  };

  const addMedicine = () => {
    const medicine = tempStates.medicine;
    if (!medicine.name) {
      alert('Por favor completa el nombre del medicamento');
      return;
    }

    onUpdate(index, {
      ...prescription,
      medicines: [...prescription.medicines, { ...medicine, id: Date.now() + Math.random() }]
    });
    resetTempState('medicine');
  };

  const removeMedicine = (medicineId) => {
    onUpdate(index, {
      ...prescription,
      medicines: prescription.medicines.filter(m => m.id !== medicineId)
    });
  };

  const addLabTest = () => {
    const labtest = tempStates.labtest;
    if (!labtest.name) {
      alert('Por favor completa el nombre del examen');
      return;
    }

    onUpdate(index, {
      ...prescription,
      labtests: [...prescription.labtests, { ...labtest, id: Date.now() + Math.random() }]
    });
    resetTempState('labtest');
  };

  const removeLabTest = (labtestId) => {
    onUpdate(index, {
      ...prescription,
      labtests: prescription.labtests.filter(l => l.id !== labtestId)
    });
  };

  return (
    <div className="card mb-3" key={`prescription-${index}-${prescription.id}`}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0 text-primary">
          <i className="bi bi-file-medical me-2"></i>
          Prescripción #{index + 1}
        </h6>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => onToggleExpand(index)}
          >
            {isExpanded ? <BsDash /> : <BsPlus />}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onRemove(index)}
          >
            <BsTrash />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="card-body">
          {/* Detalles Básicos */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">Detalles Básicos</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Paciente *</label>
                <select
                  className="form-select form-select-sm"
                  name="patientId"
                  value={prescription.patientId || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar paciente</option>
                  {patients.map(patient => (
                    <option key={`patient-${patient._id}`} value={patient._id}>
                      {patient.name} {patient.surname || patient.lastName || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Médico *</label>
                <select
                  className="form-select form-select-sm"
                  name="doctorId"
                  value={prescription.doctorId || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar médico</option>
                  {doctors.map(doctor => (
                    <option key={`doctor-${doctor._id}`} value={doctor._id}>
                      Dr. {doctor.name} {doctor.surname || doctor.lastName || ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Clínica *</label>
                <select
                  className="form-select form-select-sm"
                  name="clinicId"
                  value={prescription.clinicId || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar clínica</option>
                  {clinics.map(clinic => (
                    <option key={`clinic-${clinic._id}`} value={clinic._id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Condición Médica */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">Condición Médica</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Nombre de la Condición</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  name="conditionname"
                  value={prescription.condition?.conditionname || ''}
                  onChange={(e) => {
                    console.log('Condition name change:', e.target.value); // Debug
                    handleConditionChange(e);
                  }}
                  placeholder="ej. Hipertensión arterial"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Estado Actual</label>
                <select
                  className="form-select form-select-sm"
                  name="currentstatus"
                  value={prescription.condition?.currentstatus || 'ongoing'}
                  onChange={handleConditionChange}
                >
                  <option value="ongoing">En curso</option>
                  <option value="resolved">Resuelto</option>
                  <option value="observation">En observación</option>
                </select>
              </div>
            </div>
          </div>

          {/* Síntomas y Sugerencias */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">Síntomas y Sugerencias</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Síntomas</label>
                <textarea
                  className="form-control"
                  name="symptoms"
                  value={prescription.symptoms || ''}
                  onChange={(e) => {
                    console.log('Symptoms change:', e.target.value); // Debug
                    handleInputChange(e);
                  }}
                  rows="2"
                  placeholder="Síntomas del paciente"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Sugerencias</label>
                <textarea
                  className="form-control"
                  name="suggestion"
                  value={prescription.suggestion || ''}
                  onChange={(e) => {
                    console.log('Suggestion change:', e.target.value); // Debug
                    handleInputChange(e);
                  }}
                  rows="2"
                  placeholder="Sugerencias médicas"
                />
              </div>
            </div>
          </div>

          {/* Enfermedades */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">
              Enfermedades 
              <span className="badge bg-secondary ms-2">{prescription.diseases?.length || 0}</span>
            </h6>
            
            <div className="bg-light p-3 rounded mb-3">
              <div className="row">
                <div className="col-md-6 mb-2">
                  <select
                    className="form-select form-select-sm"
                    value={tempStates.disease.diseaseId || ''}
                    onChange={(e) => {
                      const diseaseId = e.target.value;
                      const selectedDisease = diseases.find(d => d._id === diseaseId);
                      setTempStates(prev => ({
                        ...prev,
                        disease: {
                          ...prev.disease,
                          diseaseId: diseaseId,
                          name: selectedDisease ? selectedDisease.name || selectedDisease.diseaseName || selectedDisease.template?.name || '' : prev.disease.name
                        }
                      }));
                    }}
                  >
                    <option value="">Seleccionar enfermedad</option>
                    {diseases.map(d => (
                      <option key={`disease-${d._id}`} value={d._id}>
                        {d.name || d.diseaseName || d.template?.name || 'Sin nombre disponible'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nombre de la enfermedad"
                    value={tempStates.disease.name || ''}
                    onChange={(e) => updateTempState('disease', 'name', e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm"
                  value={tempStates.disease.severity || 'moderate'}
                  onChange={(e) => updateTempState('disease', 'severity', e.target.value)}
                >
                  <option value="low">Baja</option>
                  <option value="moderate">Moderada</option>
                  <option value="high">Alta</option>
                </select>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Duración"
                  value={tempStates.disease.duration || ''}
                  onChange={(e) => updateTempState('disease', 'duration', e.target.value)}
                />
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={addDisease}
                  type="button"
                >
                  <BsPlus />
                </button>
              </div>
            </div>

            {prescription.diseases && prescription.diseases.length > 0 && (
              <div className="mb-2">
                {prescription.diseases.map((d) => (
                  <div key={`disease-item-${d.id}`} className="d-flex justify-content-between align-items-center bg-white p-2 border rounded mb-1">
                    <div>
                      <strong>{d.name}</strong>
                      <span className={`badge ms-2 ${
                        d.severity === 'high' ? 'bg-danger' : 
                        d.severity === 'moderate' ? 'bg-warning' : 'bg-success'
                      }`}>
                        {d.severity === 'high' ? 'Alta' : d.severity === 'moderate' ? 'Moderada' : 'Baja'}
                      </span>
                      {d.duration && <small className="text-muted ms-2">({d.duration})</small>}
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeDisease(d.id)}
                      type="button"
                    >
                      <BsTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Medicamentos */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">
              Medicamentos 
              <span className="badge bg-secondary ms-2">{prescription.medicines?.length || 0}</span>
            </h6>
            
            <div className="bg-light p-3 rounded mb-3">
              <div className="row">
                <div className="col-md-6 mb-2">
                  <select
                    className="form-select form-select-sm"
                    value={tempStates.medicine.medicineId || ''}
                    onChange={(e) => {
                      const medicineId = e.target.value;
                      const selectedMedicine = medicines.find(m => m._id === medicineId);
                      setTempStates(prev => ({
                        ...prev,
                        medicine: {
                          ...prev.medicine,
                          medicineId: medicineId,
                          name: selectedMedicine ? selectedMedicine.name || selectedMedicine.medicineName : prev.medicine.name
                        }
                      }));
                    }}
                  >
                    <option value="">Seleccionar medicamento</option>
                    {medicines.map(med => (
                      <option key={`medicine-${med._id}`} value={med._id}>
                        {med.name || med.medicineName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nombre del medicamento"
                    value={tempStates.medicine.name || ''}
                    onChange={(e) => updateTempState('medicine', 'name', e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Dosis"
                  value={tempStates.medicine.dosage || ''}
                  onChange={(e) => updateTempState('medicine', 'dosage', e.target.value)}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Frecuencia"
                  value={tempStates.medicine.frequency || ''}
                  onChange={(e) => updateTempState('medicine', 'frequency', e.target.value)}
                />
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={addMedicine}
                  type="button"
                >
                  <BsPlus />
                </button>
              </div>
            </div>

            {prescription.medicines && prescription.medicines.length > 0 && (
              <div className="mb-2">
                {prescription.medicines.map((m) => (
                  <div key={`medicine-item-${m.id}`} className="d-flex justify-content-between align-items-center bg-white p-2 border rounded mb-1">
                    <div>
                      <strong>{m.name}</strong>
                      {m.dosage && <span className="text-muted ms-2">- {m.dosage}</span>}
                      {m.frequency && <span className="text-muted ms-2">({m.frequency})</span>}
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeMedicine(m.id)}
                      type="button"
                    >
                      <BsTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Exámenes de Laboratorio */}
          <div className="mb-4">
            <h6 className="text-secondary border-bottom pb-2 mb-3">
              Exámenes de Laboratorio 
              <span className="badge bg-secondary ms-2">{prescription.labtests?.length || 0}</span>
            </h6>
            
            <div className="bg-light p-3 rounded mb-3">
              <div className="row">
                <div className="col-md-6 mb-2">
                  <select
                    className="form-select form-select-sm"
                    value={tempStates.labtest.labtestId || ''}
                    onChange={(e) => {
                      const labtestId = e.target.value;
                      const selectedLabTest = labtests.find(l => l._id === labtestId);
                      setTempStates(prev => ({
                        ...prev,
                        labtest: {
                          ...prev.labtest,
                          labtestId: labtestId,
                          name: selectedLabTest ? selectedLabTest.name || selectedLabTest.testName : prev.labtest.name
                        }
                      }));
                    }}
                  >
                    <option value="">Seleccionar examen</option>
                    {labtests.map(lab => (
                      <option key={`labtest-${lab._id}`} value={lab._id}>
                        {lab.name || lab.testName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Nombre del examen"
                    value={tempStates.labtest.name || ''}
                    onChange={(e) => updateTempState('labtest', 'name', e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Resultado (opcional)"
                  value={tempStates.labtest.result || ''}
                  onChange={(e) => updateTempState('labtest', 'result', e.target.value)}
                />
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={tempStates.labtest.date || new Date().toISOString().split('T')[0]}
                  onChange={(e) => updateTempState('labtest', 'date', e.target.value)}
                />
                <button 
                  className="btn btn-sm btn-primary"
                  onClick={addLabTest}
                  type="button"
                >
                  <BsPlus />
                </button>
              </div>
            </div>

            {prescription.labtests && prescription.labtests.length > 0 && (
              <div className="mb-2">
                {prescription.labtests.map((l) => (
                  <div key={`labtest-item-${l.id}`} className="d-flex justify-content-between align-items-center bg-white p-2 border rounded mb-1">
                    <div>
                      <strong>{l.name}</strong>
                      {l.result && <span className="text-muted ms-2">- {l.result}</span>}
                      <small className="text-muted ms-2">({new Date(l.date).toLocaleDateString()})</small>
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeLabTest(l.id)}
                      type="button"
                    >
                      <BsTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente principal MultiCreatePrescription
const MultiCreatePrescription = ({ isOpen = false, onClose }) => {
  const offcanvasCloseRef = useRef(null);
  const [showSingleMode, setShowSingleMode] = useState(false);

  // Estados principales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Estados de datos
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [labtests, setLabtests] = useState([]);

  // Estados del formulario multi
  const [prescriptions, setPrescriptions] = useState([{ ...EMPTY_PRESCRIPTION_STATE, id: 1 }]);
  const [expandedCards, setExpandedCards] = useState({ 0: true });

  const handleClose = () => {
  if (onClose) {
    onClose();
  }
};
  // Funciones de API (reutilizadas del componente original)
  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${API_DOCTOR_LIST}`);
      if (!response.ok) throw new Error('Error al obtener médicos');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching doctors:', err);
      return [];
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_PATIENT_LIST}`);
      if (!response.ok) throw new Error('Error al obtener pacientes');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching patients:', err);
      return [];
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}${API_INVENTORY}`);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching medicines:', err);
      return [];
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await fetch(`${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTERS}`);
      if (!response.ok) throw new Error(`Error al obtener clínicas: ${response.status}`);
      const data = await response.json();
      return data?.data || data || [];
    } catch (err) {
      console.error('Error fetching clinics:', err);
      return [];
    }
  };

  const fetchDiseases = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_TREATMENTM_LIST}`);
      if (!response.ok) throw new Error(`Error al obtener enfermedades: ${response.status}`);
      const data = await response.json();
      
      let processedData = [];
      
      if (data.template && data.template.name) {
        processedData = [{
          _id: data._id || Date.now().toString(),
          name: data.template.name,
          diseaseName: data.template.name,
        }];
      } else if (Array.isArray(data)) {
        processedData = data.map(item => ({
          _id: item._id || Date.now().toString(),
          name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
          diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
        }));
      } else if (data?.data && Array.isArray(data.data)) {
        processedData = data.data.map(item => ({
          _id: item._id || Date.now().toString(),
          name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
          diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
        }));
      } else if (data?.data?.template?.name) {
        processedData = [{
          _id: data.data._id || data._id || Date.now().toString(),
          name: data.data.template.name,
          diseaseName: data.data.template.name,
        }];
      }
      
      return processedData;
    } catch (err) {
      console.error('Error fetching diseases:', err);
      return [];
    }
  };

  const fetchLabTests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_LAB_TEMPLATES_LIST}`);
      if (!response.ok) throw new Error('Error al obtener exámenes de laboratorio');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      return [];
    }
  };

  // Funciones de manejo de prescripciones múltiples
  const addPrescription = () => {
    const newId = prescriptions.length;
    setPrescriptions(prev => [...prev, { ...EMPTY_PRESCRIPTION_STATE, id: newId + 1 }]);
    setExpandedCards(prev => ({ ...prev, [newId]: true }));
  };

  const removePrescription = (index) => {
    if (prescriptions.length === 1) {
      alert('Debe mantener al menos una prescripción');
      return;
    }
    setPrescriptions(prev => prev.filter((_, i) => i !== index));
    setExpandedCards(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[index];
      // Reindexar las keys
      const reindexed = {};
      Object.keys(newExpanded).forEach((key, i) => {
        if (parseInt(key) > index) {
          reindexed[parseInt(key) - 1] = newExpanded[key];
        } else if (parseInt(key) < index) {
          reindexed[key] = newExpanded[key];
        }
      });
      return reindexed;
    });
  };

// En el componente MultiCreatePrescription, REEMPLAZA esta función:
const updatePrescription = (index, updatedPrescription) => {
  console.log(`updatePrescription called for index ${index}:`, updatedPrescription);
  setPrescriptions(prev => {
    console.log('Current prescriptions:', prev);
    const newPrescriptions = prev.map((p, i) => {
      if (i === index) {
        console.log(`Updating prescription at index ${i}:`, updatedPrescription);
        return {
          ...updatedPrescription,
          // Asegurar que los arrays siempre existan
          diseases: updatedPrescription.diseases || [],
          medicines: updatedPrescription.medicines || [],
          labtests: updatedPrescription.labtests || [],
          condition: updatedPrescription.condition || {...EMPTY_PRESCRIPTION_STATE.condition}
        };
      }
      return p;
    });
    console.log('New prescriptions array:', newPrescriptions);
    return newPrescriptions;
  });
};

  const toggleCardExpansion = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const duplicatePrescription = (index) => {
    const prescriptionToDuplicate = { ...prescriptions[index] };
    // Limpiar IDs únicos para la duplicación
    prescriptionToDuplicate.diseases = prescriptionToDuplicate.diseases.map(d => ({ ...d, id: Date.now() + Math.random() }));
    prescriptionToDuplicate.medicines = prescriptionToDuplicate.medicines.map(m => ({ ...m, id: Date.now() + Math.random() }));
    prescriptionToDuplicate.labtests = prescriptionToDuplicate.labtests.map(l => ({ ...l, id: Date.now() + Math.random() }));
    
    const newIndex = prescriptions.length;
    setPrescriptions(prev => [...prev, { ...prescriptionToDuplicate, id: newIndex + 1 }]);
    setExpandedCards(prev => ({ ...prev, [newIndex]: true }));
  };

  // Función de validación para múltiples prescripciones
  const validateAllPrescriptions = () => {
    for (let i = 0; i < prescriptions.length; i++) {
      const prescription = prescriptions[i];
      if (!prescription.patientId) {
        setError(`Prescripción ${i + 1}: Por favor selecciona un paciente`);
        return false;
      }
      if (!prescription.doctorId) {
        setError(`Prescripción ${i + 1}: Por favor selecciona un médico`);
        return false;
      }
      if (!prescription.clinicId) {
        setError(`Prescripción ${i + 1}: Por favor selecciona una clínica`);
        return false;
      }
    }
    return true;
  };

  // Función principal para crear múltiples prescripciones
  const createMultiplePrescriptions = async () => {
    if (!validateAllPrescriptions()) return;

    setLoading(true);
    setError(null);
    
    try {
      const prescriptionPromises = prescriptions.map(prescription => {
        const prescriptionData = {
          doctor: prescription.doctorId,
          patient: prescription.patientId,
          clinic: prescription.clinicId,
          symptoms: prescription.symptoms,
          suggestion: prescription.suggestion,
          condition: prescription.condition,
          diseases: prescription.diseases.map(disease => ({
            disease: disease.diseaseId || null,
            name: disease.name,
            description: disease.description,
            severity: disease.severity,
            duration: disease.duration
          })),
          medicines: prescription.medicines.map(medicine => ({
            medicine: medicine.medicineId || null,
            name: medicine.name,
            dosage: medicine.dosage,
            frequency: medicine.frequency,
            duration: medicine.duration,
            purpose: medicine.purpose
          })),
          labtests: prescription.labtests.map(labtest => ({
            labtest: labtest.labtestId || null,
            name: labtest.name,
            result: labtest.result,
            date: labtest.date
          }))
        };
        
        return prescriptionService.createPrescription(prescriptionData);
      });

      const results = await Promise.all(prescriptionPromises);
      
      setSuccessMessage(`✅ Se crearon exitosamente ${results.length} prescripciones`);
      
      // Limpiar formularios
      setPrescriptions([{ ...EMPTY_PRESCRIPTION_STATE, id: 1 }]);
      setExpandedCards({ 0: true });
      
      // Cerrar offcanvas después de un delay
      setTimeout(() => {
  handleClose();
  window.location.reload();
}, 2000);

    } catch (err) {
      console.error('Error creating prescriptions:', err);
      setError(err.response?.data?.message || 'Error al crear las prescripciones');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [doctorsData, patientsData, medicinesData, clinicsData, diseasesData, labtestsData] = 
          await Promise.all([
            fetchDoctors(),
            fetchPatients(),
            fetchMedicines(),
            fetchClinics(),
            fetchDiseases(),
            fetchLabTests()
          ]);

        setDoctors(doctorsData);
        setPatients(patientsData);
        setMedicines(medicinesData);
        setClinics(clinicsData);
        setDiseases(diseasesData);
        setLabtests(labtestsData);
      } catch (err) {
        setError('Error al cargar los datos iniciales');
        console.error('Error loading initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Limpiar mensajes después de un tiempo
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <>
      {/* Botón para abrir offcanvas */}
      {/* <button
        className="btn btn-success me-2"
        type="button"
        data-bs-toggle="offcanvas"
        data-bs-target="#multiCreatePrescriptionOffcanvas"
        aria-controls="multiCreatePrescriptionOffcanvas"
      >
        <i className="bi bi-plus-circle me-2"></i>
        Crear Múltiples Prescripciones
      </button> */}

      {/* Offcanvas */}
      <div
  className={`offcanvas offcanvas-end ${isOpen ? 'show' : ''}`}
  tabIndex="-1"
  id="multiCreatePrescriptionOffcanvas"
  aria-labelledby="multiCreatePrescriptionOffcanvasLabel"
  style={{ 
    width: '90%', 
    maxWidth: '1200px',
    visibility: isOpen ? 'visible' : 'hidden'
  }}
>
        <div className="offcanvas-header bg-primary text-white">
          <h5 className="offcanvas-title" id="multiCreatePrescriptionOffcanvasLabel">
            <i className="bi bi-file-medical-fill me-2"></i>
            Crear Múltiples Prescripciones
          </h5>
          <button
  type="button"
  className="btn-close btn-close-white"
  onClick={handleClose}
  aria-label="Close"
></button>
        </div>

        <div className="offcanvas-body">
          {/* Controles superiores */}
          <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center">
              <h6 className="mb-0 me-3">
                <i className="bi bi-clipboard-data me-2"></i>
                Prescripciones: 
                <span className="badge bg-primary ms-2">{prescriptions.length}</span>
              </h6>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={addPrescription}
                  disabled={loading}
                >
                  <BsPlus /> Agregar
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const allExpanded = Object.keys(expandedCards).every(key => expandedCards[key]);
                    const newExpanded = {};
                    prescriptions.forEach((_, index) => {
                      newExpanded[index] = !allExpanded;
                    });
                    setExpandedCards(newExpanded);
                  }}
                >
                  {Object.keys(expandedCards).every(key => expandedCards[key]) ? <BsDash /> : <BsPlus />}
                  {Object.keys(expandedCards).every(key => expandedCards[key]) ? 'Contraer Todo' : 'Expandir Todo'}
                </button>
              </div>
            </div>
          </div>

          {/* Mensajes de estado */}
          {error && <AlertMessage type="danger" message={error} />}
          {successMessage && <AlertMessage type="success" message={successMessage} />}

          {/* Loading spinner */}
          {loading && <LoadingSpinner />}

          {/* Lista de prescripciones */}
          {!loading && (
            <div className="prescriptions-container">
              {prescriptions.map((prescription, index) => (
                <PrescriptionCard
                  key={`${prescription.id}-${index}`}
                  prescription={prescription}
                  index={index}
                  onUpdate={updatePrescription}
                  onRemove={removePrescription}
                  patients={patients}
                  doctors={doctors}
                  clinics={clinics}
                  diseases={diseases}
                  medicines={medicines}
                  labtests={labtests}
                  isExpanded={expandedCards[index] || false}
                  onToggleExpand={toggleCardExpansion}
                />
              ))}
            </div>
          )}

          {/* Botones de acción */}
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between">
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addPrescription}
                  disabled={loading}
                >
                  <BsPlus /> Agregar Prescripción
                </button>
              </div>
              
              <div className="btn-group">
                <button
  type="button"
  className="btn btn-secondary me-2"
  onClick={handleClose}
  disabled={loading}
>
  Cancelar
</button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={createMultiplePrescriptions}
                  disabled={loading || prescriptions.length === 0}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Creando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Crear {prescriptions.length} Prescripción{prescriptions.length !== 1 ? 'es' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Información adicional */}
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              <strong>Tip:</strong> Puedes usar los controles superiores para expandir/contraer todas las prescripciones a la vez.
              Los campos marcados con (*) son obligatorios.
            </small>
          </div>
        </div>
      </div>
    </>
  );
};

export default MultiCreatePrescription;